import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TablePagination,
  TableSortLabel,
  TextField,
  Typography,
  Button,
  InputAdornment,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { HiSearch, HiPlus } from "react-icons/hi";
import { useSnackbar } from "notistack";
import { useAdminUsersPaginated, useAdminDeleteUser } from "@/api/hooks/admin.hooks";
import type { UserDto } from "@/api/types/dtos";
import type { PaginatedResponse } from "@/api/types/common";
import CreateUserModal from "./CreateUserModal";
import EditUserModal from "./EditUserModal";
import UsersTableBody from "./UsersTableBody";

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEBOUNCE_MS = 300;

type SortField = "name" | "idNumber" | "isActive";
type SortDirection = "asc" | "desc";

const UsersTable: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserDto | null>(null);
  const [deleteUser, setDeleteUser] = useState<UserDto | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, isError, refetch } = useAdminUsersPaginated({
    page,
    size: rowsPerPage,
    sortBy: sortField,
    sortDirection: sortDir.toUpperCase() as 'ASC' | 'DESC',
    search: debouncedSearch || undefined,
  });

  const deleteMutation = useAdminDeleteUser();

  const paginatedData = data?.data as PaginatedResponse<UserDto> | undefined;
  const users: UserDto[] = paginatedData?.content ?? [];
  const totalElements: number = paginatedData?.totalElements ?? 0;

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSort = useCallback(
    (field: SortField) => {
      const isAsc = sortField === field && sortDir === "asc";
      setSortDir(isAsc ? "desc" : "asc");
      setSortField(field);
      setPage(0);
    },
    [sortField, sortDir]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteUser) return;
    deleteMutation.mutate(deleteUser.id, {
      onSuccess: () => {
        enqueueSnackbar("Usuario eliminado exitosamente", { variant: "success" });
        setDeleteUser(null);
        refetch();
      },
      onError: () => {
        enqueueSnackbar("Error al eliminar el usuario", { variant: "error" });
      },
    });
  }, [deleteUser, deleteMutation, enqueueSnackbar, refetch]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <TextField
          placeholder="Buscar usuarios..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 280, flex: "1 1 280px" }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <HiSearch size={18} style={{ opacity: 0.5 }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Button
          variant="contained"
          startIcon={<HiPlus size={16} />}
          onClick={() => setCreateModalOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          Crear Usuario
        </Button>
      </Box>

      {isError ? (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography color="error" gutterBottom>
            Error al cargar los usuarios
          </Typography>
          <Button variant="outlined" onClick={() => refetch()}>
            Reintentar
          </Button>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "name"}
                      direction={sortField === "name" ? sortDir : "asc"}
                      onClick={() => handleSort("name")}
                    >
                      Nombre
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "idNumber"}
                      direction={sortField === "idNumber" ? sortDir : "asc"}
                      onClick={() => handleSort("idNumber")}
                    >
                      Documento
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "isActive"}
                      direction={sortField === "isActive" ? sortDir : "asc"}
                      onClick={() => handleSort("isActive")}
                    >
                      Estado
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <UsersTableBody
                isLoading={isLoading}
                users={users}
                debouncedSearch={debouncedSearch}
                skeletonRows={rowsPerPage}
                onEdit={setEditUser}
                onDelete={setDeleteUser}
                onCreateClick={() => setCreateModalOpen(true)}
              />
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          />
        </>
      )}

      <CreateUserModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          setCreateModalOpen(false);
          refetch();
        }}
      />

      <EditUserModal
        open={!!editUser}
        user={editUser}
        onClose={() => setEditUser(null)}
        onSuccess={() => {
          setEditUser(null);
          refetch();
        }}
      />

      <Dialog open={!!deleteUser} onClose={() => setDeleteUser(null)}>
        <DialogTitle>Eliminar Usuario</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas desactivar al usuario{" "}
            <strong>{[deleteUser?.name, deleteUser?.lastName].filter(Boolean).join(" ")}</strong>?
            Esta acción se puede revertir posteriormente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUser(null)} disabled={deleteMutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            startIcon={deleteMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {deleteMutation.isPending ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersTable;
